import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { useRef } from 'react';
import type { ReactNode } from 'react';
import { hasNestedLegacyModal, useOverlayDismissGuard } from './nestedLayerGuard.ts';
import { cx } from './cx.ts';

/**
 * Right-side sheet for detail/edit flows (Radix Dialog under the hood).
 * Replaces the page-local one-off drawers scattered across the console.
 */
export type DrawerWidth = 'sm' | 'md' | 'lg' | 'xl' | '2xl';

const WIDTH_CLASSES: Record<DrawerWidth, string> = {
  sm: 'w-[360px]',
  md: 'w-[440px]',
  lg: 'w-[560px]',
  // Full detail sheets with embedded editable tables. Existing call sites
  // depend on this exact value; the enumeration lives in
  // test/drawer-width.test.tsx.
  xl: 'w-[896px]',
  // Wide sheets whose table does not fit `xl` (#1492): at 896px the content
  // area is only ~842px after the px-5 padding, 134px short of the routing
  // config sheet's 976px target table, so it scrolled horizontally. 1400px
  // leaves ~1360px of content — room for that table plus headroom; the
  // arithmetic is asserted in test/drawer-width.test.tsx. The
  // `max-w-[92vw]` already on the content element clamps this on narrow
  // viewports, which makes the effective width min(1400px, 92vw).
  '2xl': 'w-[1400px]',
};

interface Props {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: ReactNode;
  readonly description?: ReactNode;
  readonly children: ReactNode;
  readonly footer?: ReactNode;
  readonly width?: DrawerWidth;
  /** Accessible name for the close button — pass a translated string. */
  readonly closeLabel: string;
  readonly testId?: string;
  readonly onEscapeKeyDown?: (event: KeyboardEvent) => void;
}

export function Drawer({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  width = 'md',
  closeLabel,
  testId,
  onEscapeKeyDown,
}: Props): JSX.Element {
  // Block self-dismiss when the outside interaction belongs to a nested Radix
  // popper (e.g. a `Select` opened inside this sheet). See nestedLayerGuard.
  const { onInteractOutside } = useOverlayDismissGuard(open);
  const contentRef = useRef<HTMLDivElement | null>(null);

  /**
   * A legacy `components/Modal` opened inside this drawer renders inline and
   * outside Radix's layer stack, so without this both surfaces answer the
   * same Escape and the drawer tears down under a half-filled form. The modal
   * closes itself on Escape; the drawer must let the innermost layer win and
   * stay put. Callers can still override via `onEscapeKeyDown`.
   */
  function handleEscapeKeyDown(event: KeyboardEvent): void {
    if (hasNestedLegacyModal(contentRef.current)) {
      event.preventDefault();
      return;
    }
    onEscapeKeyDown?.(event);
  }

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/60 animate-overlay-in" />
        <DialogPrimitive.Content
          ref={contentRef}
          data-testid={testId}
          onInteractOutside={onInteractOutside}
          onEscapeKeyDown={handleEscapeKeyDown}
          className={cx(
            'fixed inset-y-0 right-0 z-50 flex max-w-[92vw] flex-col border-l border-line bg-card shadow-overlay animate-drawer-in focus:outline-none',
            WIDTH_CLASSES[width],
          )}
        >
          <div className="flex items-start gap-3 border-b border-line-soft px-5 py-4">
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <DialogPrimitive.Title className="m-0 text-title text-paper">{title}</DialogPrimitive.Title>
              {description !== undefined && description !== null ? (
                <DialogPrimitive.Description className="m-0 text-dense text-paper-mid">
                  {description}
                </DialogPrimitive.Description>
              ) : null}
            </div>
            <DialogPrimitive.Close
              aria-label={closeLabel}
              className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded text-paper-low transition-colors duration-120 hover:bg-active hover:text-paper focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40"
            >
              <X aria-hidden="true" className="h-4 w-4" />
            </DialogPrimitive.Close>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
          {footer !== undefined && footer !== null ? (
            <div className="flex items-center justify-end gap-2 border-t border-line-soft px-5 py-3.5">{footer}</div>
          ) : null}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
